import * as A from 'astronomy-engine';
import type { GeoLocation } from '../../config/world';

export interface AltAz {
  altDeg: number;
  azDeg: number;
}

export interface MoonState extends AltAz {
  illuminatedFraction: number;
  phaseAngleDeg: number;
  /** Lit-limb midpoint direction, degrees from zenith toward celestial east. */
  brightLimbAngleDeg: number;
  distanceKm: number;
}

export type PlanetName = 'venus' | 'mars' | 'jupiter' | 'saturn';

export interface PlanetState extends AltAz {
  magnitude: number;
}

export type Twilight = 'day' | 'civil' | 'nautical' | 'astronomical' | 'night';

export interface SkyState {
  sun: AltAz;
  moon: MoonState;
  planets: Record<PlanetName, PlanetState>;
  localSiderealTimeHours: number;
  twilight: Twilight;
  darkness: number;
}

const D2R = Math.PI / 180;
const AU_KM = 149_597_870.7;
const PLANETS: Record<PlanetName, A.Body> = {
  venus: A.Body.Venus,
  mars: A.Body.Mars,
  jupiter: A.Body.Jupiter,
  saturn: A.Body.Saturn,
};

const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));
const norm180 = (deg: number): number => ((((deg % 360) + 540) % 360) - 180);

function observerOf(loc: GeoLocation): A.Observer {
  return new A.Observer(loc.latitude, loc.longitude, loc.elevationM);
}

function locate(body: A.Body, date: Date, obs: A.Observer): { altAz: AltAz; eq: A.EquatorialCoordinates } {
  const eq = A.Equator(body, date, obs, true, true);
  const hor = A.Horizon(date, obs, eq.ra, eq.dec, 'normal');
  return { altAz: { altDeg: hor.altitude, azDeg: hor.azimuth }, eq };
}

/** Meeus ch. 48: position angle of the bright limb (chi) minus the parallactic angle (q). */
function brightLimbAngle(sun: A.EquatorialCoordinates, moon: A.EquatorialCoordinates, lstHours: number, latDeg: number): number {
  const a0 = sun.ra * 15 * D2R;
  const d0 = sun.dec * D2R;
  const a = moon.ra * 15 * D2R;
  const d = moon.dec * D2R;
  const chi = Math.atan2(Math.cos(d0) * Math.sin(a0 - a), Math.sin(d0) * Math.cos(d) - Math.cos(d0) * Math.sin(d) * Math.cos(a0 - a));
  const h = (lstHours - moon.ra) * 15 * D2R;
  const phi = latDeg * D2R;
  const q = Math.atan2(Math.sin(h), Math.tan(phi) * Math.cos(d) - Math.sin(d) * Math.cos(h));
  return norm180((chi - q) / D2R);
}

export function twilightOf(sunAltDeg: number): Twilight {
  if (sunAltDeg > -0.833) return 'day';
  if (sunAltDeg > -6) return 'civil';
  if (sunAltDeg > -12) return 'nautical';
  if (sunAltDeg > -18) return 'astronomical';
  return 'night';
}

export function darknessOf(sunAltDeg: number, moonAltDeg: number, moonFraction: number): number {
  const night = clamp01((-sunAltDeg - 4) / 14);
  const moonUp = clamp01((moonAltDeg + 1) / 11);
  const moonLight = moonUp * Math.pow(clamp01(moonFraction), 1.5);
  return night * (1 - 0.6 * moonLight);
}

export function computeSky(instantMs: number, loc: GeoLocation): SkyState {
  const date = new Date(instantMs);
  const obs = observerOf(loc);
  const sun = locate(A.Body.Sun, date, obs);
  const moon = locate(A.Body.Moon, date, obs);
  const illum = A.Illumination(A.Body.Moon, date);
  const lst = (((A.SiderealTime(date) + loc.longitude / 15) % 24) + 24) % 24;

  const planets = {} as Record<PlanetName, PlanetState>;
  for (const [name, body] of Object.entries(PLANETS) as Array<[PlanetName, A.Body]>) {
    planets[name] = { ...locate(body, date, obs).altAz, magnitude: A.Illumination(body, date).mag };
  }

  return {
    sun: sun.altAz,
    moon: {
      ...moon.altAz,
      illuminatedFraction: illum.phase_fraction,
      phaseAngleDeg: illum.phase_angle,
      brightLimbAngleDeg: brightLimbAngle(sun.eq, moon.eq, lst, loc.latitude),
      distanceKm: moon.eq.dist * AU_KM,
    },
    planets,
    localSiderealTimeHours: lst,
    twilight: twilightOf(sun.altAz.altDeg),
    darkness: darknessOf(sun.altAz.altDeg, moon.altAz.altDeg, illum.phase_fraction),
  };
}

export function findSunEvent(kind: 'rise' | 'set', fromMs: number, loc: GeoLocation): number | null {
  const t = A.SearchRiseSet(A.Body.Sun, observerOf(loc), kind === 'rise' ? 1 : -1, new Date(fromMs), 1);
  return t ? t.date.getTime() : null;
}
